
import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import MyWeek from '../components/dashboard/MyWeek';
import TeamOverview from '../components/dashboard/TeamOverview';
import PeriodSwitch from '../components/dashboard/PeriodSwitch';
import ReportsPanel from '../components/dashboard/ReportsPanel';
import { rangeLabel } from '../components/dashboard/dashboardUtils';
import {
  Box,
  Grid,
  Card,
  CardContent,
  Typography,
  CircularProgress,
  Alert,
  useTheme,
  TextField,
  Button,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Chip,
  LinearProgress,
  Avatar,
  Divider,
  Fade,
  Grow,
  Slide,
  alpha,
  useMediaQuery,
  Autocomplete,
  Tooltip,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
} from '@mui/material';
import { useAuth } from '../contexts/AuthContext';
import { usePermissions } from '../hooks/usePermissions';
import { getProjectDisplayKey } from '../utils/projectDisplay';
import StatusPill from '../components/StatusPill';
import ToolbarButton from '../components/ToolbarButton';
import PageHeader from '../components/PageHeader';
import StatCard from '../components/StatCard';
import FilterBar from '../components/FilterBar';
import { getGreetingTheme } from '../utils/greetingTheme';
import { WaveLoading } from '../components/Loader';
import {
  ResponsiveContainer,
  PieChart,
  Pie,
  Cell,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Legend,
  Tooltip as RechartTooltip,
  LineChart,
  Line,
  AreaChart,
  Area,
} from 'recharts';
import {
  Download as DownloadIcon,
  TrendingUp as TrendingUpIcon,
  TrendingDown as TrendingDownIcon,
  Assessment as AssessmentIcon,
  Refresh as RefreshIcon,
  AddRounded as AddIcon,
  Dashboard as DashboardIcon,
  People as PeopleIcon,
  Work as WorkIcon,
  Schedule as ScheduleIcon,
  AttachMoney as MoneyIcon,
  Coffee as CoffeeIcon,
  Restaurant as RestaurantIcon,
  MeetingRoom as MeetingIcon,
  MoreHoriz as MoreIcon,
  FilterList as FilterIcon,
  Group as GroupIcon,
  Assignment as AssignmentIcon,
  Business as ProjectIcon,
  AccessTime as TimeIcon,
  EventNote as EventIcon,
  Clear as ClearIcon,
  ArrowBack as ArrowBackIcon,
} from '@mui/icons-material';

const statLabels = [
  { 
    key: 'totalProjects', 
    label: 'Total Projects', 
    icon: <ProjectIcon />, 
    color: 'primary',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
  },
  { 
    key: 'totalTasks', 
    label: 'Total Tasks', 
    icon: <AssignmentIcon />, 
    color: 'secondary',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
  },
  { 
    key: 'totalUsers', 
    label: 'Total Users', 
    icon: <PeopleIcon />, 
    color: 'success',
    gradient: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)'
  },
  { 
    key: 'totalHours', 
    label: 'Total Hours Logged', 
    icon: <TimeIcon />, 
    color: 'info',
    gradient: 'linear-gradient(135deg, #43e97b 0%, #38f9d7 100%)'
  },
  { 
    key: 'totalTransactions', 
    label: 'Total Transactions', 
    icon: <MoneyIcon />, 
    color: 'warning',
    gradient: 'linear-gradient(135deg, #fa709a 0%, #fee140 100%)'
  },
  { 
    key: 'totalBreaks', 
    label: 'Total Breaks', 
    icon: <CoffeeIcon />, 
    color: 'error',
    gradient: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
  },
  { 
    key: 'totalBreakHours', 
    label: 'Break Hours', 
    icon: <RestaurantIcon />, 
    color: 'primary',
    gradient: 'linear-gradient(135deg, #ff6b6b 0%, #ee5a24 100%)'
  },
  { 
    key: 'activeProjects', 
    label: 'Active Projects', 
    icon: <WorkIcon />, 
    color: 'success',
    gradient: 'linear-gradient(135deg, #ff9a9e 0%, #fecfef 100%)'
  },
  { 
    key: 'completedTasks', 
    label: 'Completed Tasks', 
    icon: <AssessmentIcon />, 
    color: 'info',
    gradient: 'linear-gradient(135deg, #a8caba 0%, #5d4e75 100%)'
  },
  { 
    key: 'pendingTasks', 
    label: 'Pending Tasks', 
    icon: <ScheduleIcon />, 
    color: 'warning',
    gradient: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)'
  },
];

// Status/priority pie charts and "transactions per task" were removed in the
// Oct 2026 dashboard revamp: task statuses are free text (25+ spellings) and
// 95% of entries have no transaction type, so those charts were noise.
const reportList = [
  {
    key: 'projectReport',
    label: 'Project Overview', 
    icon: <DashboardIcon />, 
    description: 'Comprehensive project performance metrics',
    color: 'primary'
  },
  {
    key: 'projectHours',
    label: 'Hours by Project',
    icon: <ProjectIcon />,
    description: 'Hours logged per project with potential number, service and date added',
    color: 'success'
  },
  {
    key: 'loggedVsScanned',
    label: 'Logged vs Scanned Hours',
    icon: <ScheduleIcon />,
    description: 'Hours logged in FlatForm compared with biometric scanned-in hours, per person per day',
    color: 'error',
    restrictedRoles: ['admin', 'project_manager', 'team_leader', 'cmo']
  },
  {
    key: 'dailyUserLogs',
    label: 'Daily Time Logs',
    icon: <TimeIcon />, 
    description: 'Daily time tracking and productivity insights',
    color: 'secondary'
  },
  { 
    key: 'monthlyUserLogs', 
    label: 'Monthly Time Logs', 
    icon: <EventIcon />, 
    description: 'Monthly aggregated time and transaction data',
    color: 'info'
  },
  { 
    key: 'breaksLeisure', 
    label: 'Breaks & Leisure Report', 
    icon: <CoffeeIcon />, 
    description: 'Comprehensive break and leisure time analysis',
    color: 'error'
  },
  { 
    key: 'agentWiseDaily', 
    label: 'Agent-wise Daily Report', 
    icon: <GroupIcon />, 
    description: 'Daily transaction and break analysis per employee (9hr office schedule)',
    color: 'primary'
  },
  { 
    key: 'consolidatedAgentDaily', 
    label: 'Consolidated Agent Daily', 
    icon: <GroupIcon />, 
    description: 'Consolidated agent/task/project rows with breaks & hours',
    color: 'secondary'
  },
  { 
    key: 'assigneeTasksReport', 
    label: 'Assignee Tasks Report', 
    icon: <AssignmentIcon />, 
    description: 'Task assignments with project details and assignees',
    color: 'info',
    restrictedRoles: ['admin', 'project_manager', 'cmo'],
    restrictedServiceId: 35  // Marcom service ID
  }
];

// Mapping of report keys to API endpoints and table keys
const reportConfig = {
  projectReport: { endpoint: 'project-report', title: 'Project Overview' },
  // Built client-side from /projects + /time-transactions (no dedicated endpoint)
  projectHours: { endpoint: null, title: 'Hours by Project' },
  loggedVsScanned: { endpoint: null, title: 'Logged vs Scanned Hours' },
  dailyUserLogs: { endpoint: 'user-daily-logs', title: 'Daily Time Logs' },
  monthlyUserLogs: { endpoint: 'user-monthly-logs', title: 'Monthly Time Logs' },
  breaksLeisure: { endpoint: 'breaks-leisure', title: 'Breaks & Leisure Report' },
  agentWiseDaily: { endpoint: 'agent-wise-daily', title: 'Agent-wise Daily Report' },
  consolidatedAgentDaily: { endpoint: 'agent-consolidated-daily', title: 'Consolidated Agent Daily' },
  assigneeTasksReport: { endpoint: 'assignee-tasks-report', title: 'Assignee Tasks Report' }
};

const Dashboard = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const { user } = useAuth();
  const navigate = useNavigate();
  const { canPerformActionByRoute } = usePermissions();
  const [dateRange, setDateRange] = useState({ startDate: '', endDate: '' });
  const [appliedDateRange, setAppliedDateRange] = useState({ startDate: '', endDate: '' });
  const [dateErrors, setDateErrors] = useState({ startDate: '', endDate: '' });
  // User filter (multi-select). selectedUsers = in-progress chips in the
  // Autocomplete; appliedUsers = the snapshot that drives data fetches +
  // downloads, so cards only refresh when "Apply Filter" is clicked.
  const [users, setUsers] = useState([]);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [appliedUsers, setAppliedUsers] = useState([]);
  const [downloading, setDownloading] = useState(false);
  // Overview = "My week" + Team view, for the selected period.
  const [overview, setOverview] = useState(null);
  const [period, setPeriod] = useState('week');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedReport, setSelectedReport] = useState(null);
  const [reportData, setReportData] = useState([]);
  const [projectHoursServiceId, setProjectHoursServiceId] = useState('');
  const [reportServices, setReportServices] = useState([]);
  // Logged vs Scanned report filters
  const [lvsServiceId, setLvsServiceId] = useState('');
  const [lvsFlaggedOnly, setLvsFlaggedOnly] = useState(true);
  const [loadingReport, setLoadingReport] = useState(false);
  const [errorReport, setErrorReport] = useState('');
  const [refreshKey, setRefreshKey] = useState(0);
  
  // Enhanced color palette for charts
  // Chart palette — kept in sync with the StatCard accent colors so
  // pies/bars match the surrounding summary cards.
  const COLORS = [
    "#3b82f6", // blue
    "#10b981", // green
    "#8b5cf6", // violet
    "#f59e0b", // amber
    "#ec4899", // pink
    "#06b6d4", // cyan
    "#ef4444", // red
    "#14b8a6", // teal
    "#f97316", // orange
    "#6366f1", // indigo
  ];

  const todayStr = (() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-${String(t.getDate()).padStart(2,'0')}`; })();
  const monthStartStr = (() => { const t = new Date(); return `${t.getFullYear()}-${String(t.getMonth()+1).padStart(2,'0')}-01`; })();
  const [dailyDate, setDailyDate] = useState(todayStr);
  const [reportStartDate, setReportStartDate] = useState(monthStartStr);
  const [reportEndDate, setReportEndDate] = useState(todayStr);
  const [monthlyFilter, setMonthlyFilter] = useState(() => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  });
  const [assigneeStartDate, setAssigneeStartDate] = useState('');
  const [assigneeEndDate, setAssigneeEndDate] = useState('');
  // Per-report Employee filter (used by Daily Time Logs).
  const [reportSelectedUsers, setReportSelectedUsers] = useState([]);

  // Format role string for display ("team_member" -> "Team Member").
  const formatRoleLabel = (role) => {
    if (!role) return '';
    return role.replace(/_/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
  };
  // Look up a user's role for a Daily Logs row (match by email, fall back to name).
  const getRoleForLog = (row) => {
    if (!row || !users.length) return '';
    let u = null;
    if (row.email) {
      const e = row.email.toLowerCase();
      u = users.find((x) => x.email && x.email.toLowerCase() === e);
    }
    if (!u && row.user) {
      u = users.find((x) => x.name === row.user);
    }
    return formatRoleLabel(u?.role);
  };
  // Apply the Daily Logs Employee filter to a row list. Lenient match because
  // the report rows and the user records come from different APIs and can
  // differ in name format ("Vikas.Ba" vs "Vikas Bashetti").
  const filterDailyLogsByUsers = (rows) => {
    if (!Array.isArray(rows) || !reportSelectedUsers.length) return rows || [];
    const norm = (s) => String(s || '').trim().toLowerCase();
    const ids = new Set(reportSelectedUsers.map((u) => u.id).filter((v) => v != null));
    const emails = new Set(reportSelectedUsers.map((u) => norm(u.email)).filter(Boolean));
    const fullNames = new Set(reportSelectedUsers.map((u) => norm(u.name)).filter(Boolean));
    const namePrefixes = new Set();
    reportSelectedUsers.forEach((u) => {
      const first = norm(u.name).split(/[\s.]+/)[0];
      if (first) namePrefixes.add(first);
    });
    return rows.filter((r) => {
      const rid = r.userId ?? r.user_id ?? r.id;
      if (rid != null && ids.has(rid)) return true;
      const rowEmail = r.email || r.userEmail || r.employeeEmail || '';
      const rowName = r.user || r.userName || r.employeeName || r.name || '';
      if (rowEmail && emails.has(norm(rowEmail))) return true;
      if (rowName) {
        const rn = norm(rowName);
        if (fullNames.has(rn)) return true;
        for (const fn of fullNames) {
          if (fn.includes(rn) || rn.includes(fn)) return true;
        }
        const rFirst = rn.split(/[\s.]+/)[0];
        if (rFirst && namePrefixes.has(rFirst)) return true;
      }
      return false;
    });
  };

  useEffect(() => {
    let cancelled = false;
    const fetchOverview = async () => {
      // Keep the old data on screen while a new period loads; only block on first load.
      if (!overview) setLoading(true);
      setError('');
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(
          `${process.env.REACT_APP_API_URL}/dashboard/overview?period=${encodeURIComponent(period)}`,
          { headers: { Authorization: `Bearer ${token}` } }
        );
        if (!res.ok) throw new Error('Failed to load the dashboard');
        const data = await res.json();
        if (!cancelled) setOverview(data);
      } catch (err) {
        if (!cancelled) setError(err.message);
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    fetchOverview();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [period, refreshKey]);

  // Fetch the list of users once, for the User filter Autocomplete
  useEffect(() => {
    const fetchUsersForFilter = async () => {
      try {
        const token = localStorage.getItem('token');
        const res = await fetch(`${process.env.REACT_APP_API_URL}/users/for-project-management`, {
          headers: { Authorization: `Bearer ${token}` },
        });
        if (!res.ok) {
          // Fallback to /users/active if the preferred endpoint isn't available
          const fb = await fetch(`${process.env.REACT_APP_API_URL}/users/active`, {
            headers: { Authorization: `Bearer ${token}` },
          });
          if (!fb.ok) throw new Error('Failed to fetch users');
          const fbData = await fb.json();
          setUsers(Array.isArray(fbData) ? fbData : fbData.data || []);
          return;
        }
        const data = await res.json();
        setUsers(Array.isArray(data) ? data : data.data || []);
      } catch (err) {
        console.error('[Dashboard] Failed to load users for filter:', err);
        setUsers([]);
      }
    };
    fetchUsersForFilter();
  }, []);

  // Handler for fetching report data
  const fetchReportData = async (key, extraParams = {}) => {
    setSelectedReport(key);
    setLoadingReport(true);
    setErrorReport('');
    setReportData([]);
    try {
      const token = localStorage.getItem('token');
      const { endpoint } = reportConfig[key];
      const urlParams = new URLSearchParams(extraParams).toString();
      const res = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/reports/${endpoint}?${urlParams}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(`Failed to fetch report (${res.status})${body.message ? `: ${body.message}` : ''}`);
      }
      const data = await res.json();
      setReportData(data);
    } catch (err) {
      setErrorReport(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  // One row per employee + project + milestone + sprint + task + calendar month.
  // The backend now groups by month and returns { month: 'YYYY-MM', daysLogged };
  // older backends returned one row per day, so this also rolls those up.
  const aggregateMonthlyLogs = (rows) => {
    if (!Array.isArray(rows)) return [];
    const groups = new Map();
    rows.forEach((row) => {
      let monthKey = /^\d{4}-\d{2}$/.test(row.month || '') ? row.month : '';
      if (!monthKey && row.date) {
        const d = new Date(row.date);
        if (!Number.isNaN(d.getTime())) {
          monthKey = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
        }
      }
      const key = [row.email || row.user, row.project, row.milestone, row.sprint, row.task, monthKey].join('|');
      const hours = parseFloat(row.hours) || 0;
      const preCounted = Number.isFinite(row.daysLogged) ? row.daysLogged : null;
      let g = groups.get(key);
      if (!g) {
        const [y, m] = monthKey.split('-').map(Number);
        g = {
          user: row.user,
          email: row.email,
          project: row.project,
          milestone: row.milestone,
          sprint: row.sprint,
          task: row.task,
          monthKey,
          month: monthKey
            ? new Date(y, m - 1, 1).toLocaleDateString(undefined, { month: 'short', year: 'numeric' })
            : '',
          hours: 0,
          days: new Set(),
          dayCount: 0,
        };
        groups.set(key, g);
      }
      g.hours += hours;
      if (preCounted !== null) g.dayCount += preCounted;
      else if (row.date) g.days.add(String(row.date).slice(0, 10));
    });
    return [...groups.values()]
      .map((g) => ({ ...g, hours: Math.round(g.hours * 100) / 100, daysLogged: g.dayCount + g.days.size }))
      .sort((a, b) =>
        (a.user || '').localeCompare(b.user || '') ||
        a.monthKey.localeCompare(b.monthKey) ||
        (a.project || '').localeCompare(b.project || ''));
  };

  // Hours by Project — served by GET /dashboard/reports/project-hours. Falls back
  // to joining /projects + /time-transactions client-side if the backend hasn't
  // been deployed with that route yet (404).
  const fetchProjectHoursReport = async (startDate, endDate) => {
    setSelectedReport('projectHours');
    setLoadingReport(true);
    setErrorReport('');
    setReportData([]);
    try {
      const token = localStorage.getItem('token');
      const headers = { Authorization: `Bearer ${token}` };

      if (!reportServices.length) {
        fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, { headers })
          .then((r) => (r.ok ? r.json() : []))
          .then((s) => setReportServices(Array.isArray(s) ? s : []))
          .catch(() => {});
      }

      const reportParams = new URLSearchParams({ startDate, endDate });
      const reportRes = await fetch(
        `${process.env.REACT_APP_API_URL}/dashboard/reports/project-hours?${reportParams.toString()}`,
        { headers }
      );
      if (reportRes.ok) {
        const data = await reportRes.json();
        setReportData((Array.isArray(data) ? data : []).map((r) => ({
          ...r,
          displayKey: getProjectDisplayKey(r),
          potentialId: r.potentialId || '',
          serviceName: r.serviceName || r.service?.Service || '',
          manager: r.manager || '',
        })));
        return;
      }
      if (reportRes.status !== 404) throw new Error('Failed to fetch project hours report');

      // ---- Fallback: backend without the project-hours route ----
      const txParams = new URLSearchParams({ page: 1, limit: 100000 });
      if (startDate) txParams.append('startDate', startDate);
      if (endDate) txParams.append('endDate', endDate);

      const [projRes, txRes] = await Promise.all([
        fetch(`${process.env.REACT_APP_API_URL}/projects?page=1&limit=10000`, { headers }),
        fetch(`${process.env.REACT_APP_API_URL}/time-transactions?${txParams.toString()}`, { headers }),
      ]);
      if (!projRes.ok) throw new Error('Failed to fetch projects');
      if (!txRes.ok) throw new Error('Failed to fetch time entries');

      const projJson = await projRes.json();
      const projects = Array.isArray(projJson) ? projJson : projJson.data || [];
      const txJson = await txRes.json();
      const entries = Array.isArray(txJson) ? txJson : txJson.data || [];

      const totals = new Map();
      entries.forEach((e) => {
        const pid = e.task?.projectId ?? e.task?.Project?.id ?? e.projectId;
        if (pid == null) return;
        const t = totals.get(pid) || { hours: 0, entries: 0, users: new Set() };
        t.hours += parseFloat(e.hours) || 0;
        t.entries += 1;
        if (e.userId ?? e.user?.id) t.users.add(e.userId ?? e.user?.id);
        totals.set(pid, t);
      });

      const rows = projects
        .map((p) => {
          const t = totals.get(p.id);
          return {
            id: p.id,
            name: p.name,
            displayKey: getProjectDisplayKey(p),
            potentialId: p.potentialId || '',
            serviceId: p.service?.Id ?? p.serviceId ?? null,
            serviceName: p.service?.Service || '',
            manager: p.manager?.name || '',
            status: p.status || '',
            createdAt: p.createdAt || null,
            hours: t ? Math.round(t.hours * 100) / 100 : 0,
            entries: t ? t.entries : 0,
            contributors: t ? t.users.size : 0,
          };
        })
        .filter((r) => r.hours > 0)
        .sort((a, b) => b.hours - a.hours);

      setReportData(rows);
    } catch (err) {
      setErrorReport(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  // Logged vs Scanned — GET /dashboard/reports/logged-vs-scanned. Rows are one
  // person-day; status is 'over' | 'no_scan' | 'no_employee_id' | 'ok'.
  const fetchLoggedVsScanned = async (startDate, endDate, serviceId = lvsServiceId) => {
    setSelectedReport('loggedVsScanned');
    setLoadingReport(true);
    setErrorReport('');
    setReportData([]);
    try {
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      if (!reportServices.length) {
        fetch(`${process.env.REACT_APP_API_URL}/service-management/services`, { headers })
          .then((r) => (r.ok ? r.json() : []))
          .then((s) => setReportServices(Array.isArray(s) ? s : []))
          .catch(() => {});
      }
      const params = new URLSearchParams({ startDate, endDate });
      if (serviceId) params.append('serviceId', serviceId);
      const res = await fetch(`${process.env.REACT_APP_API_URL}/dashboard/reports/logged-vs-scanned?${params}`, { headers });
      if (!res.ok) {
        const body = await res.json().catch(() => ({}));
        throw new Error(`Failed to fetch report (${res.status})${body.message ? `: ${body.message}` : ''}`);
      }
      const data = await res.json();
      setReportData(Array.isArray(data.rows) ? data.rows : []);
    } catch (err) {
      setErrorReport(err.message);
    } finally {
      setLoadingReport(false);
    }
  };

  const LVS_STATUS = {
    over: { label: 'Over scanned', color: 'error.main' },
    no_scan: { label: 'No scan', color: 'warning.main' },
    no_employee_id: { label: 'No Employee ID', color: 'text.secondary' },
    ok: { label: 'OK', color: 'success.main' },
  };

  const getFilteredLoggedVsScanned = () => {
    if (!Array.isArray(reportData)) return [];
    return lvsFlaggedOnly ? reportData.filter((r) => r.status === 'over' || r.status === 'no_scan') : reportData;
  };

  const getFilteredProjectHours = () => {
    if (!Array.isArray(reportData)) return [];
    if (!projectHoursServiceId) return reportData;
    return reportData.filter((r) => String(r.serviceId) === String(projectHoursServiceId));
  };

  // Download a CSV of time-transactions matching the currently applied
  // Date Range + User filters. This is the "share with client" export at the
  // page header — distinct from any per-report download below.
  const handleDownloadFilteredReport = async () => {
    setDownloading(true);
    try {
      const token = localStorage.getItem('token');
      const params = new URLSearchParams();
      if (appliedDateRange.startDate) params.append('startDate', appliedDateRange.startDate);
      if (appliedDateRange.endDate) params.append('endDate', appliedDateRange.endDate);
      // Pull a large page so we get everything in the date range
      params.append('page', 1);
      params.append('limit', 100000);

      const res = await fetch(
        `${process.env.REACT_APP_API_URL}/time-transactions?${params.toString()}`,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      if (!res.ok) throw new Error('Failed to fetch time entries');
      const data = await res.json();
      let rows = Array.isArray(data) ? data : data.data || [];

      // Apply user filter client-side so this works regardless of whether the
      // backend supports a userIds parameter on /time-transactions.
      if (appliedUsers.length) {
        const wanted = new Set(appliedUsers.map(u => u.id));
        rows = rows.filter(e => wanted.has(e.userId) || wanted.has(e.user?.id));
      }

      if (!rows.length) {
        setError('No time entries match the current filters');
        setDownloading(false);
        return;
      }

      const escape = (v) => {
        if (v === null || v === undefined) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };

      const headers = ['Employee', 'Project', 'Task', 'File Name', 'Date', 'Hours', 'Transactions', 'Type'];
      const csvRows = [headers.join(',')];
      let totalHours = 0;
      let totalTransactions = 0;
      rows.forEach((e) => {
        totalHours += parseFloat(e.hours) || 0;
        totalTransactions += parseFloat(e.transactions) || 0;
        csvRows.push([
          escape(e.user?.name || ''),
          escape(e.task?.Project?.name || ''),
          escape(e.task?.title || ''),
          escape(e.fileName || ''),
          escape(e.date),
          escape(e.hours),
          escape(e.transactions),
          escape(e.transactionType || ''),
        ].join(','));
      });

      // Totals row at the bottom
      csvRows.push([
        '', '', '', '',
        'Total',
        escape(totalHours.toFixed(2)),
        escape(totalTransactions),
        '',
      ].join(','));

      const blob = new Blob(['﻿' + csvRows.join('\n')], { type: 'text/csv;charset=utf-8' });
      const url = URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;

      let filename = `dashboard_report_${new Date().toISOString().split('T')[0]}`;
      if (appliedDateRange.startDate && appliedDateRange.endDate) {
        filename += `_${appliedDateRange.startDate}_to_${appliedDateRange.endDate}`;
      }
      if (appliedUsers.length === 1) {
        filename += `_${appliedUsers[0].name.replace(/\s+/g, '-')}`;
      } else if (appliedUsers.length > 1) {
        filename += `_${appliedUsers.length}-users`;
      }
      filename += '.csv';
      link.download = filename;

      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      URL.revokeObjectURL(url);
    } catch (err) {
      console.error('[Dashboard] Download failed:', err);
      setError(err.message || 'Failed to download report');
    } finally {
      setDownloading(false);
    }
  };

  const handleReportClick = (key) => {
    if (key === 'dailyUserLogs' || key === 'monthlyUserLogs') {
      // Fetch immediately with default 7-day range
      fetchReportData(key, { startDate: reportStartDate, endDate: reportEndDate });
    } else if (key === 'projectHours') {
      setProjectHoursServiceId('');
      fetchProjectHoursReport(reportStartDate, reportEndDate);
    } else if (key === 'loggedVsScanned') {
      setLvsServiceId('');
      setLvsFlaggedOnly(true);
      fetchLoggedVsScanned(reportStartDate, reportEndDate, '');
    } else if (key === 'agentWiseDaily' || key === 'consolidatedAgentDaily') {
      // Single-date reports - show date picker, user clicks Load
      setSelectedReport(key);
      setReportData([]);
      setErrorReport('');
    } else if (key === 'assigneeTasksReport') {
      // Fetch all data immediately, user can filter by date later
      setAssigneeStartDate('');
      setAssigneeEndDate('');
      fetchReportData(key);
    } else {
      fetchReportData(key);
    }
  };

  // Refresh dashboard data
  const handleRefresh = () => setRefreshKey(prev => prev + 1);

  // Export current report to CSV
  const exportReportCsv = () => {
    if (!selectedReport) return;
    
    // Check if report data exists based on report type
    if (selectedReport === 'breaksLeisure') {
      if (!reportData.detailedBreaks || reportData.detailedBreaks.length === 0) return;
    } else if (selectedReport === 'projectReport') {
      if (!reportData || reportData.length === 0) return;
    } else if (selectedReport === 'dailyUserLogs' || selectedReport === 'monthlyUserLogs') {
      if (!reportData || reportData.length === 0) return;
    } else if (selectedReport === 'agentWiseDaily') {
      if (!reportData.agentReports || reportData.agentReports.length === 0) return;
    } else if (selectedReport === 'consolidatedAgentDaily') {
      if (!reportData.rows || reportData.rows.length === 0) return;
    } else if (selectedReport === 'assigneeTasksReport') {
      if (!reportData || reportData.length === 0) return;
    } else {
      if (!reportData || reportData.length === 0) return;
    }
    
    const { labelKey, valueKey, title } = reportConfig[selectedReport];
    let headers = [];
    let csvRows = [];

    if (selectedReport === 'loggedVsScanned') {
      const escape = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = getFilteredLoggedVsScanned();
      if (!rows.length) {
        setErrorReport('No rows to export');
        return;
      }
      headers = ['Employee', 'Email', 'Employee ID', 'Date', 'First In', 'Last Out', 'Scanned Hours', 'Hours Inside', 'Logged Hours', 'Difference', 'Status'];
      csvRows = [headers.join(',')];
      rows.forEach((r) => {
        csvRows.push([
          escape(r.name), escape(r.email), escape(r.employeeId), escape(r.date),
          escape(r.firstIn), escape(r.lastOut), escape(r.scannedHours), escape(r.insideHours),
          escape(r.loggedHours), escape(r.difference), escape(LVS_STATUS[r.status]?.label || r.status),
        ].join(','));
      });
    } else if (selectedReport === 'projectHours') {
      const escape = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      const rows = getFilteredProjectHours();
      if (!rows.length) {
        setErrorReport('No projects match the selected service');
        return;
      }
      headers = ['Project', 'Project Key', 'Potential', 'Service', 'Manager', 'Status', 'Date Added', 'Contributors', 'Entries', 'Hours Logged'];
      csvRows = [headers.join(',')];
      let total = 0;
      rows.forEach((r) => {
        total += r.hours;
        csvRows.push([
          escape(r.name),
          escape(r.displayKey),
          escape(r.potentialId),
          escape(r.serviceName),
          escape(r.manager),
          escape(r.status),
          escape(r.createdAt ? String(r.createdAt).slice(0, 10) : ''),
          escape(r.contributors),
          escape(r.entries),
          escape(r.hours),
        ].join(','));
      });
      csvRows.push(['', '', '', '', '', '', '', '', 'Total', escape(total.toFixed(2))].join(','));
    } else if (selectedReport === 'dailyUserLogs' || selectedReport === 'monthlyUserLogs') {
      const isDaily = selectedReport === 'dailyUserLogs';
      headers = isDaily
        ? ['Employee Name','Email','Role','Project','Milestone','Sprint','Task','Hours','Date']
        : ['Employee Name','Email','Project','Milestone','Sprint','Task','Month','Days Logged','Hours'];
      csvRows = [headers.join(',')];
      const escape = (v) => {
        if (v == null) return '';
        const s = String(v);
        return /[",\n\r]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
      };
      // Daily Logs: respect the Employee filter; Monthly: rolled up per month.
      const rowsForExport = isDaily ? filterDailyLogsByUsers(reportData) : aggregateMonthlyLogs(reportData);
      if (isDaily && reportSelectedUsers.length && rowsForExport.length === 0) {
        setErrorReport('No rows match the selected employees');
        return;
      }
      let totalHours = 0;
      rowsForExport.forEach((row) => {
        totalHours += parseFloat(row.hours) || 0;
        const cells = isDaily
          ? [
              escape(row.user),
              escape(row.email),
              escape(getRoleForLog(row)),
              escape(row.project),
              escape(row.milestone),
              escape(row.sprint),
              escape(row.task),
              escape(row.hours),
              escape(row.date ? new Date(row.date).toLocaleDateString() : ''),
            ]
          : [
              escape(row.user),
              escape(row.email),
              escape(row.project),
              escape(row.milestone),
              escape(row.sprint),
              escape(row.task),
              escape(row.month),
              escape(row.daysLogged),
              escape(row.hours),
            ];
        csvRows.push(cells.join(','));
      });
      // Totals row (Hours summed; other columns blank)
      const totalsCells = isDaily
        ? ['', '', '', '', '', '', 'Total', escape(totalHours.toFixed(2)), '']
        : ['', '', '', '', '', '', '', 'Total', escape(totalHours.toFixed(2))];
      csvRows.push(totalsCells.join(','));
    } else if (selectedReport === 'breaksLeisure') {
      headers = ['Employee','Type','Description','Start Time','End Time','Duration (Hours)','Date','Status'];
      csvRows = [headers.join(',')];
      reportData.detailedBreaks.forEach((row) => {
        csvRows.push([
          row.employee,
          row.type,
          row.description || '',
          row.startTime,
          row.endTime || '',
          row.durationHours,
          row.date,
          row.isActive ? 'Active' : 'Completed'
        ].join(','));
      });
    } else if (selectedReport === 'projectReport') {
      headers = ['Project Name','Projects This Month','Tasks Today','Records Logged','Records Processed','Avg Minutes per Record','Total Hours','Records per Agent','Time per Agent'];
      csvRows = [headers.join(',')];
      reportData.forEach((row) => {
        const recordsPerAgent = row.recordsPerAgent?.map(a => `${a.user}:${a.count}`).join('; ') || '';
        const timePerAgent = row.timePerAgent?.map(a => `${a.user}:${a.hours}hrs`).join('; ') || '';
        csvRows.push([
          row.projectName,
          row.projectsReceived,
          row.tasksToday,
          row.recordsLogged,
          row.recordsProcessed,
          row.avgMinutesPerRecord,
          row.totalHours,
          recordsPerAgent,
          timePerAgent
        ].join(','));
      });
    } else if (selectedReport === 'agentWiseDaily') {
      headers = ['Agent','Date','Work Hours','Transactions','Transactions/Hour','Break Hours','Break Count','Work Compliance %','Break Compliance %','Overall Compliance %'];
      csvRows = [headers.join(',')];
      reportData.agentReports.forEach((agent) => {
        csvRows.push([
          agent.agent.name,
          agent.date,
          agent.workMetrics.totalWorkHours,
          agent.workMetrics.totalTransactions,
          agent.workMetrics.transactionsPerHour,
          agent.breakMetrics.totalBreakHours,
          agent.breakMetrics.totalBreaks,
          agent.workMetrics.workHoursCompliance,
          agent.breakMetrics.breakHoursCompliance,
          agent.officeHours.overallCompliance
        ].join(','));
      });
    } else if (selectedReport === 'consolidatedAgentDaily') {
      headers = ['Project','Potential ID','Task','Employee','Task Status','Total Hours','Worked Hours','Transactions','Break Hours','Other Hours'];
      csvRows = [headers.join(',')];
      reportData.rows.forEach((row) => {
        csvRows.push([
          row.projectName,
          row.potentialId,
          row.taskName,
          row.teamMember,
          row.taskStatus,
          row.totalHours,
          row.workedHours,
          row.transactions,
          row.breakHours,
          row.otherHours
        ].join(','));
      });
    } else if (selectedReport === 'assigneeTasksReport') {
      headers = ['Project','Task','Start Date','End Date','Requester','Task Status','Project Manager','Task Assigned','Iterations'];
      csvRows = [headers.join(',')];
      reportData.forEach((row) => {
        csvRows.push([
          `"${(row.project || '').replace(/"/g, '""')}"`,
          `"${(row.task || '').replace(/"/g, '""')}"`,
          row.startDate || '',
          row.endDate || '',
          `"${(row.requester || '').replace(/"/g, '""')}"`,
          row.taskStatus || '',
          `"${(row.projectManager || '').replace(/"/g, '""')}"`,
          `"${(row.taskAssigned || '').replace(/"/g, '""')}"`,
          row.iterations || 1
        ].join(','));
      });
    } else {
      headers = [labelKey, valueKey];
      csvRows = [headers.join(',')];
    reportData.forEach(row => {
      csvRows.push([row[labelKey], row[valueKey]].join(','));
    });
    }
    
    const csvString = csvRows.join('\n');
    const blob = new Blob([csvString], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `${title.replace(/\s+/g,'_')}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  if (loading) return (
    <Box 
      sx={{
        minHeight: 'calc(100vh - 120px)',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 2,
        bgcolor: 'transparent',
      }}
    >
      <WaveLoading size="large" />
      <Typography
        sx={{
          fontSize: '0.875rem',
          color: 'text.secondary',
          fontWeight: 500,
        }}
      >
        Loading dashboard…
      </Typography>
    </Box>
  );
  
  if (error) return (
    <Box p={3}>
      <Alert 
        severity="error" 
        sx={{ 
          mt: 4, 
          borderRadius: 2,
          '& .MuiAlert-icon': { fontSize: 28 }
        }}
        action={
          <IconButton
            color="inherit"
            size="small"
            onClick={handleRefresh}
          >
            <RefreshIcon />
          </IconButton>
        }
      >
        {error}
      </Alert>
    </Box>
  );
  
  if (!overview) return null;

  return (
    <Box
      sx={{
        minHeight: '100vh',
        bgcolor: theme.palette.mode === 'dark' ? '#17181c' : '#fafbfc',
        p: { xs: 2, md: 4 },
      }}
    >
      <Box sx={{ maxWidth: 1200, mx: 'auto' }}>
      {/* When a report is selected, the page becomes that report.
          A back arrow returns to the dashboard view. */}
      {selectedReport && (
        <PageHeader
          title={reportConfig[selectedReport]?.title || 'Report'}
          actions={
            <ToolbarButton
              variant="outlined"
              startIcon={<ArrowBackIcon />}
              onClick={() => {
                setSelectedReport(null);
                setReportData([]);
                setErrorReport('');
              }}
            >
              Back to dashboard
            </ToolbarButton>
          }
        />
      )}

      {!selectedReport && (
      <>
      {/* Header — time-of-day gradient greeting */}
      {/* My week — for people who log time. Team view — manager roles only.
          Admins, CMOs and view-only users don't fill timesheets, so they go
          straight to the team view. The greeting panel is rendered by the
          view itself so its stat chips can sit inside it. */}
      {(() => {
        const hour = new Date().getHours();
        const firstName = user?.name ? user.name.split(' ')[0] : '';
        const themeData = getGreetingTheme({ hour });
        // Quick actions for managers: the two things they most often leave the
        // dashboard to do. Members get their Log time button in My week.
        const isAdmin = user?.role === 'admin';
        const canAddTask = overview.team && (isAdmin || canPerformActionByRoute('/tasks', 'Add'));
        const canAddProject = overview.team && (isAdmin || canPerformActionByRoute('/projects', 'Add'));
        const quickSx = {
          fontWeight: 700, borderRadius: 999, px: 3, py: 1.25, fontSize: '0.8125rem', whiteSpace: 'nowrap',
          color: 'text.primary', bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.08)' : alpha('#fff', 0.7),
          '&:hover': { bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.12)' : '#fff' },
        };
        const header = {
          title: firstName ? `${themeData.greeting}, ${firstName}` : themeData.greeting,
          subtitle: rangeLabel(overview.startDate, overview.endDate),
          actions: (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, flexWrap: 'wrap' }}>
              {canAddTask && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => navigate('/tasks')} sx={quickSx}>New task</Button>
              )}
              {canAddProject && (
                <Button size="small" startIcon={<AddIcon />} onClick={() => navigate('/projects')} sx={quickSx}>New project</Button>
              )}
              <PeriodSwitch value={period} onChange={setPeriod} />
              <IconButton onClick={handleRefresh} size="small" sx={{ color: 'text.secondary' }} title="Refresh">
                <RefreshIcon />
              </IconButton>
            </Box>
          ),
        };
        // The reports are a tool, not the dashboard: a compact index in the side rail.
        const allowed = (item) => {
          const roleAllowed = !item.restrictedRoles || item.restrictedRoles.includes(user?.role);
          const serviceAllowed = !item.restrictedServiceId || user?.role === 'admin' || user?.serviceid === item.restrictedServiceId;
          return roleAllowed && serviceAllowed;
        };
        const reportsPanel = <ReportsPanel reports={reportList.filter(allowed)} onOpen={handleReportClick} />;
        const logsTime = !['admin', 'cmo', 'view_only', 'QC', 'partner'].includes(user?.role);
        return (
          <Box sx={{ display: 'flex', flexDirection: 'column', gap: 4, mb: 8, opacity: loading ? 0.6 : 1, transition: 'opacity 0.2s' }}>
            {/* Anyone without a team view (members, QC, partners) gets their own week + reports. */}
            {!overview.team && (
              <MyWeek
                overview={overview}
                header={header}
                onLogTime={() => navigate('/time-transactions')}
                onViewTasks={() => navigate('/tasks')}
                reports={reportsPanel}
              />
            )}
            {overview.team && (
              <TeamOverview
                overview={overview}
                header={header}
                user={user}
                navigate={navigate}
                personal={logsTime ? (
                  <MyWeek
                    overview={overview}
                    compact
                    onLogTime={() => navigate('/time-transactions')}
                    onViewTasks={() => navigate('/tasks')}
                  />
                ) : null}
                reports={reportsPanel}
                onOpenReport={handleReportClick}
              />
            )}
          </Box>
        );
      })()}

      </>
      )}
      {/* Report Details — renders inline when a report is selected */}
      {loadingReport && (
        <Box
          sx={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 2,
            minHeight: 300,
            bgcolor: 'background.paper',
            border: theme.palette.mode === 'dark'
              ? '1px solid rgba(255,255,255,0.08)'
              : '1px solid #e5e7eb',
            borderRadius: 2.5,
            p: 5,
            boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
          }}
        >
          <WaveLoading theme={theme} size="large" />
          <Typography
            sx={{
              fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
              fontSize: '0.9375rem',
              fontWeight: 600,
              color: 'text.primary',
            }}
          >
            Loading report data...
          </Typography>
        </Box>
      )}
      
      {errorReport && (
        <Alert 
          severity="error" 
          sx={{ 
            mt: 2, 
            borderRadius: 2,
            '& .MuiAlert-icon': { fontSize: 24 }
          }}
        >
          {errorReport}
        </Alert>
      )}
      
      {selectedReport && !loadingReport && !errorReport && (
        <Fade in timeout={300}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: theme.palette.mode === 'dark'
                ? '1px solid rgba(255,255,255,0.08)'
                : '1px solid #e5e7eb',
              borderRadius: 2.5,
              p: { xs: 2, md: 3 },
              boxShadow: theme.palette.mode === 'dark' ? 'none' : '0 1px 2px rgba(0,0,0,0.04)',
            }}
          >
          {selectedReport === 'projectReport' ? (
            <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: 440,
                borderRadius: 2.5,
                boxShadow: 'none',
                bgcolor: 'background.paper',
                border: (t) => t.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.08)'
                  : '1px solid #e5e7eb',
              }}>
              <Table size="small" stickyHeader>
                <TableHead>
                  <TableRow>
                    {['Project','Projects This Month','Tasks Today','Records Logged','Records Processed','Avg Minutes per Record','Total Hours','Records per Agent','Time per Agent'].map((header) => (
                      <TableCell
                        key={header}
                        sx={{
                          backgroundColor: theme.palette.mode === 'light' ? theme.palette.grey[200] : theme.palette.grey[800],
                          fontWeight: 'bold',
                          whiteSpace: 'nowrap',
                          position: 'sticky',
                          top: 0,
                        }}
                      >
                        {header}
                      </TableCell>
                    ))}
                  </TableRow>
                </TableHead>
                <TableBody>
                  {reportData.map((row) => (
                    <TableRow key={row.projectName} hover>
                      <TableCell sx={{ whiteSpace: 'nowrap', fontWeight: 500 }}>{row.projectName}</TableCell>
                      <TableCell>{row.projectsReceived}</TableCell>
                      <TableCell>{row.tasksToday}</TableCell>
                      <TableCell>{row.recordsLogged}</TableCell>
                      <TableCell>{row.recordsProcessed}</TableCell>
                      <TableCell>{row.avgMinutesPerRecord}</TableCell>
                      <TableCell>{row.totalHours}</TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {row.recordsPerAgent.map((a) => (
                            <Chip key={a.user} label={`${a.user} - ${a.count}`} size="small" />
                          ))}
                        </Box>
                      </TableCell>
                      <TableCell>
                        <Box display="flex" flexWrap="wrap" gap={0.5}>
                          {row.timePerAgent.map((a) => (
                            <Chip key={a.user} label={`${a.user} - ${a.hours} hrs`} size="small" />
                          ))}
                        </Box>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          ) : selectedReport === 'loggedVsScanned' ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  px: 2,
                  py: 1.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                  borderRadius: 2,
                }}
              >
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: reportEndDate || undefined }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: reportStartDate || undefined }}
                />
                {user?.role === 'admin' && !user?.serviceid && (
                  <FormControl size="small" sx={{ minWidth: 200 }}>
                    <InputLabel>Service</InputLabel>
                    <Select label="Service" value={lvsServiceId} onChange={(e) => setLvsServiceId(e.target.value)}>
                      <MenuItem value="">All services</MenuItem>
                      {reportServices.map((s) => (
                        <MenuItem key={s.Id} value={String(s.Id)}>{s.Service}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                )}
                <FormControl size="small" sx={{ minWidth: 170 }}>
                  <InputLabel>Show</InputLabel>
                  <Select label="Show" value={lvsFlaggedOnly ? 'flagged' : 'all'} onChange={(e) => setLvsFlaggedOnly(e.target.value === 'flagged')}>
                    <MenuItem value="flagged">Flagged days only</MenuItem>
                    <MenuItem value="all">All days</MenuItem>
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={() => fetchLoggedVsScanned(reportStartDate, reportEndDate)}
                  disabled={!reportStartDate || !reportEndDate}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Apply Filter
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportReportCsv}
                  disabled={getFilteredLoggedVsScanned().length === 0}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Download Report
                </Button>
              </Box>
              {(() => {
                const rows = getFilteredLoggedVsScanned();
                const all = Array.isArray(reportData) ? reportData : [];
                const overCount = all.filter((r) => r.status === 'over').length;
                const noScanCount = all.filter((r) => r.status === 'no_scan').length;
                const headerSx = {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)',
                  color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                };
                const fmt = (n) => (n == null ? '—' : Math.round(n * 100) / 100);
                return (
                  <>
                    <Box sx={{ mb: 1.5, fontSize: '0.8125rem', color: 'text.secondary' }}>
                      <Box component="span" sx={{ color: 'error.main', fontWeight: 600 }}>{overCount} day{overCount === 1 ? '' : 's'} over scanned</Box>
                      {' · '}
                      <Box component="span" sx={{ color: 'warning.main', fontWeight: 600 }}>{noScanCount} day{noScanCount === 1 ? '' : 's'} with hours logged but no scan</Box>
                      {' · Scanned = first to last biometric scan of the day. Today is never flagged.'}
                    </Box>
                    <TableContainer
                      component={Paper}
                      elevation={0}
                      sx={{
                        maxHeight: 520,
                        borderRadius: 2.5,
                        boxShadow: 'none',
                        bgcolor: 'background.paper',
                        border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                      }}
                    >
                      <Table size="small" stickyHeader>
                        <TableHead>
                          <TableRow>
                            {['Employee', 'Date', 'In', 'Out', 'Scanned', 'Logged', 'Difference', 'Status'].map((h) => (
                              <TableCell key={h} sx={headerSx} align={['Scanned', 'Logged', 'Difference'].includes(h) ? 'right' : 'left'}>{h}</TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.length === 0 ? (
                            <TableRow>
                              <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                                {lvsFlaggedOnly ? 'No flagged days in this range.' : 'No logged or scanned hours in this range.'}
                              </TableCell>
                            </TableRow>
                          ) : rows.map((r) => {
                            const st = LVS_STATUS[r.status] || LVS_STATUS.ok;
                            const over = r.status === 'over';
                            return (
                              <TableRow
                                key={`${r.userId}|${r.date}`}
                                hover
                                sx={over ? { bgcolor: theme.palette.mode === 'dark' ? 'rgba(239,68,68,0.10)' : '#fef2f2' } : undefined}
                              >
                                <TableCell>
                                  <Box sx={{ fontWeight: 500 }}>{r.name}</Box>
                                  <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>
                                    {r.employeeId ? `ID ${r.employeeId}` : r.email}
                                  </Box>
                                </TableCell>
                                <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                  {new Date(`${r.date}T00:00:00`).toLocaleDateString(undefined, { weekday: 'short', month: 'short', day: 'numeric' })}
                                </TableCell>
                                <TableCell>{r.firstIn || '—'}</TableCell>
                                <TableCell>
                                  {r.lastOut || '—'}
                                  {r.missingOut && (
                                    <Tooltip title="Day ended on an IN punch (no OUT punch)">
                                      <Box component="span" sx={{ ml: 0.5, color: 'warning.main' }}>*</Box>
                                    </Tooltip>
                                  )}
                                </TableCell>
                                <TableCell align="right">{fmt(r.scannedHours)}</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 600, color: over ? 'error.main' : undefined }}>{fmt(r.loggedHours)}</TableCell>
                                <TableCell align="right" sx={{ color: over ? 'error.main' : 'text.secondary' }}>
                                  {r.difference == null ? '—' : `${r.difference > 0 ? '+' : ''}${fmt(r.difference)}`}
                                </TableCell>
                                <TableCell sx={{ color: st.color, fontWeight: 600, whiteSpace: 'nowrap' }}>{st.label}</TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </TableContainer>
                  </>
                );
              })()}
            </>
          ) : selectedReport === 'projectHours' ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  px: 2,
                  py: 1.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                  borderRadius: 2,
                }}
              >
                <TextField
                  type="date"
                  label="From"
                  size="small"
                  value={reportStartDate}
                  onChange={(e) => setReportStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ max: reportEndDate || undefined }}
                />
                <TextField
                  type="date"
                  label="To"
                  size="small"
                  value={reportEndDate}
                  onChange={(e) => setReportEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ min: reportStartDate || undefined }}
                />
                <FormControl size="small" sx={{ minWidth: 200 }}>
                  <InputLabel>Service</InputLabel>
                  <Select
                    label="Service"
                    value={projectHoursServiceId}
                    onChange={(e) => setProjectHoursServiceId(e.target.value)}
                  >
                    <MenuItem value="">All services</MenuItem>
                    {reportServices.map((s) => (
                      <MenuItem key={s.Id} value={String(s.Id)}>{s.Service}</MenuItem>
                    ))}
                  </Select>
                </FormControl>
                <Button
                  variant="contained"
                  onClick={() => fetchProjectHoursReport(reportStartDate, reportEndDate)}
                  disabled={!reportStartDate || !reportEndDate}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Apply Filter
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportReportCsv}
                  disabled={getFilteredProjectHours().length === 0}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Download Report
                </Button>
              </Box>
              {(() => {
                const rows = getFilteredProjectHours();
                const totalHours = rows.reduce((sum, r) => sum + r.hours, 0);
                const headerSx = {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)',
                  color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                  fontWeight: 'bold',
                  whiteSpace: 'nowrap',
                };
                return (
                  <TableContainer
                    component={Paper}
                    elevation={0}
                    sx={{
                      maxHeight: 480,
                      borderRadius: 2.5,
                      boxShadow: 'none',
                      bgcolor: 'background.paper',
                      border: (t) => t.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                    }}
                  >
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['Project', 'Potential', 'Service', 'Manager', 'Status', 'Date Added', 'Contributors', 'Hours Logged'].map((h) => (
                            <TableCell key={h} sx={headerSx} align={h === 'Hours Logged' || h === 'Contributors' ? 'right' : 'left'}>{h}</TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {rows.length === 0 ? (
                          <TableRow>
                            <TableCell colSpan={8} align="center" sx={{ py: 5, color: 'text.secondary' }}>
                              No hours logged against any project in this range{projectHoursServiceId ? ' for the selected service' : ''}.
                            </TableCell>
                          </TableRow>
                        ) : rows.map((r) => (
                          <TableRow key={r.id} hover>
                            <TableCell>
                              <Box sx={{ fontWeight: 500 }}>{r.name}</Box>
                              <Box sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{r.displayKey}</Box>
                            </TableCell>
                            <TableCell sx={{ fontFamily: r.potentialId ? 'ui-monospace, SFMono-Regular, Menlo, monospace' : undefined }}>
                              {r.potentialId || '—'}
                            </TableCell>
                            <TableCell>{r.serviceName || '—'}</TableCell>
                            <TableCell>{r.manager || '—'}</TableCell>
                            <TableCell><StatusPill status={r.status} /></TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }}>
                              {r.createdAt ? new Date(r.createdAt).toLocaleDateString() : '—'}
                            </TableCell>
                            <TableCell align="right">{r.contributors}</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 600 }}>{r.hours}</TableCell>
                          </TableRow>
                        ))}
                        {rows.length > 0 && (
                          <TableRow>
                            <TableCell colSpan={7} sx={{ fontWeight: 700 }}>Total ({rows.length} project{rows.length === 1 ? '' : 's'})</TableCell>
                            <TableCell align="right" sx={{ fontWeight: 700 }}>{Math.round(totalHours * 100) / 100}</TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
            </>
          ) : selectedReport === 'dailyUserLogs' || selectedReport === 'monthlyUserLogs' ? (
            <>
              {selectedReport === 'dailyUserLogs' && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    px: 2,
                    py: 1.5,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                    borderRadius: 2,
                  }}
                >
                  <TextField
                    type="date"
                    label="From"
                    size="small"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: reportEndDate || undefined, lang: 'en-GB' }}
                  />
                  <TextField
                    type="date"
                    label="To"
                    size="small"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: reportStartDate || undefined, lang: 'en-GB' }}
                  />
                  <Autocomplete
                    multiple
                    size="small"
                    options={users}
                    value={reportSelectedUsers}
                    onChange={(_, newValue) => {
                      setReportSelectedUsers(newValue);
                      // Clearing the chip(s) acts as "clear filter" — empty
                      // the table immediately rather than re-fetching everyone.
                      if (newValue.length === 0) {
                        setReportData([]);
                        setErrorReport('');
                        return;
                      }
                      // Picking an employee always pulls a wide 5-year window
                      // so we ignore whatever the date pickers happen to be
                      // pre-filled with. The user can still narrow afterward
                      // via the date pickers + Apply Filter.
                      const fiveYearsAgo = new Date();
                      fiveYearsAgo.setFullYear(fiveYearsAgo.getFullYear() - 5);
                      fetchReportData('dailyUserLogs', {
                        startDate: fiveYearsAgo.toISOString().slice(0, 10),
                        endDate: new Date().toISOString().slice(0, 10),
                        userIds: newValue.map((u) => u.id).join(','),
                      });
                    }}
                    getOptionLabel={(option) => option.name || ''}
                    isOptionEqualToValue={(o, v) => o.id === v.id}
                    sx={{ minWidth: 240, maxWidth: 360 }}
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Employee"
                        placeholder={reportSelectedUsers.length ? '' : 'All employees'}
                      />
                    )}
                  />
                  <Button
                    variant="contained"
                    onClick={() => fetchReportData('dailyUserLogs', { startDate: reportStartDate, endDate: reportEndDate })}
                    disabled={!reportStartDate || !reportEndDate}
                    size="small"
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Apply Filter
                  </Button>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      setReportSelectedUsers([]);
                      setReportStartDate(monthStartStr);
                      setReportEndDate(todayStr);
                      setReportData([]);
                      setErrorReport('');
                    }}
                    disabled={
                      reportSelectedUsers.length === 0 &&
                      reportStartDate === monthStartStr &&
                      reportEndDate === todayStr &&
                      (!reportData || reportData.length === 0)
                    }
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Clear Filter
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportReportCsv}
                    disabled={!reportData || reportData.length === 0}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Download Report
                  </Button>
                </Box>
              )}
              {selectedReport === 'monthlyUserLogs' && (
                <Box
                  sx={{
                    mb: 2,
                    display: 'flex',
                    gap: 1.5,
                    alignItems: 'center',
                    flexWrap: 'wrap',
                    px: 2,
                    py: 1.5,
                    bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                    border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                    borderRadius: 2,
                  }}
                >
                  <TextField
                    type="date"
                    label="From"
                    size="small"
                    value={reportStartDate}
                    onChange={(e) => setReportStartDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ max: reportEndDate || undefined, lang: 'en-GB' }}
                  />
                  <TextField
                    type="date"
                    label="To"
                    size="small"
                    value={reportEndDate}
                    onChange={(e) => setReportEndDate(e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    inputProps={{ min: reportStartDate || undefined, lang: 'en-GB' }}
                  />
                  <Button
                    variant="contained"
                    onClick={() => fetchReportData('monthlyUserLogs', { startDate: reportStartDate, endDate: reportEndDate })}
                    disabled={!reportStartDate || !reportEndDate}
                    size="small"
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Apply Filter
                  </Button>
                  <Box sx={{ flexGrow: 1 }} />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<DownloadIcon />}
                    onClick={exportReportCsv}
                    disabled={!reportData || reportData.length === 0}
                    sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                  >
                    Download Report
                  </Button>
                </Box>
              )}
              <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: 440,
                borderRadius: 2.5,
                boxShadow: 'none',
                bgcolor: 'background.paper',
                border: (t) => t.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.08)'
                  : '1px solid #e5e7eb',
              }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {(selectedReport === 'dailyUserLogs'
                        ? ['Employee Name','Email','Role','Project','Milestone','Sprint','Task','Hours','Date']
                        : ['Employee Name','Email','Project','Milestone','Sprint','Task','Month','Days Logged','Hours']
                      ).map((header) => (
                        <TableCell
                          key={header}
                          sx={{
                            backgroundColor: theme.palette.mode === 'dark'
                              ? 'rgba(44, 48, 52, 0.8)'
                              : 'rgba(0, 0, 0, 0.05)',
                            color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                            fontWeight: 'bold',
                            position: 'sticky',
                            top: 0,
                          }}
                        >
                          {header}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {selectedReport === 'dailyUserLogs'
                      ? filterDailyLogsByUsers(reportData).map((row, idx) => (
                        <TableRow key={`${row.user}-${row.task}-${row.date || idx}`} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{row.user || '-'}</TableCell>
                          <TableCell>{row.email || '-'}</TableCell>
                          <TableCell>{getRoleForLog(row) || '-'}</TableCell>
                          <TableCell>{row.project || '-'}</TableCell>
                          <TableCell>{row.milestone || '-'}</TableCell>
                          <TableCell>{row.sprint || '-'}</TableCell>
                          <TableCell>{row.task || '-'}</TableCell>
                          <TableCell>{row.hours}</TableCell>
                          <TableCell>{row.date ? new Date(row.date).toLocaleDateString() : '-'}</TableCell>
                        </TableRow>
                      ))
                      : aggregateMonthlyLogs(reportData).map((row, idx) => (
                        <TableRow key={`${row.email}-${row.project}-${row.task}-${row.monthKey}-${idx}`} hover>
                          <TableCell sx={{ fontWeight: 500 }}>{row.user || '-'}</TableCell>
                          <TableCell>{row.email || '-'}</TableCell>
                          <TableCell>{row.project || '-'}</TableCell>
                          <TableCell>{row.milestone || '-'}</TableCell>
                          <TableCell>{row.sprint || '-'}</TableCell>
                          <TableCell>{row.task || '-'}</TableCell>
                          <TableCell sx={{ whiteSpace: 'nowrap' }}>{row.month || '-'}</TableCell>
                          <TableCell>{row.daysLogged}</TableCell>
                          <TableCell sx={{ fontWeight: 600 }}>{row.hours}</TableCell>
                        </TableRow>
                      ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : selectedReport === 'agentWiseDaily' ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  px: 2,
                  py: 1.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                  borderRadius: 2,
                }}
              >
                <TextField
                  type="date"
                  label="Select Date"
                  size="small"
                  value={dailyDate}
                  onChange={(e) => setDailyDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'en-GB' }}
                />
                <Button
                  variant="contained"
                  onClick={() => fetchReportData('agentWiseDaily', { date: dailyDate })}
                  disabled={!dailyDate}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Apply Filter
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportReportCsv}
                  disabled={!reportData?.agentReports || reportData.agentReports.length === 0}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Download Report
                </Button>
              </Box>

              {/* Team Summary */}
              {reportData.teamSummary && (
                <Box mb={4}>
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 1.5,
                    }}
                  >
                    Team Summary
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(3, 1fr)', lg: 'repeat(6, 1fr)' },
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <StatCard label="Total Agents" value={reportData.teamSummary.totalAgents} accent="#3b82f6" />
                    <StatCard label="Total Work Hours" value={`${reportData.teamSummary.totalWorkHours.toFixed(1)}h`} accent="#10b981" />
                    <StatCard label="Total Transactions" value={reportData.teamSummary.totalTransactions.toLocaleString()} accent="#06b6d4" />
                    <StatCard label="Total Breaks" value={reportData.teamSummary.totalBreaks} accent="#f59e0b" />
                    <StatCard label="Avg Compliance" value={`${reportData.teamSummary.averageCompliance.toFixed(1)}%`} accent="#8b5cf6" />
                    <StatCard label="Top Performer" value={reportData.teamSummary.topPerformer?.agent?.name || 'N/A'} accent="#ec4899" />
                  </Box>

                  {/* Compliance Breakdown */}
                  <Typography
                    sx={{
                      fontSize: '0.75rem',
                      fontWeight: 700,
                      color: 'text.secondary',
                      textTransform: 'uppercase',
                      letterSpacing: '0.08em',
                      mb: 1.5,
                    }}
                  >
                    Compliance Breakdown
                  </Typography>
                  <Box
                    sx={{
                      display: 'grid',
                      gridTemplateColumns: { xs: '1fr', md: 'repeat(3, 1fr)' },
                      gap: 2,
                      mb: 3,
                    }}
                  >
                    <StatCard label="Fully Compliant (≥90%)" value={reportData.teamSummary.complianceBreakdown.fullyCompliant} accent="#10b981" />
                    <StatCard label="Partially Compliant (70-89%)" value={reportData.teamSummary.complianceBreakdown.partiallyCompliant} accent="#f59e0b" />
                    <StatCard label="Non-Compliant (<70%)" value={reportData.teamSummary.complianceBreakdown.nonCompliant} accent="#ef4444" />
                  </Box>
                </Box>
              )}

              {/* Individual Agent Reports */}
              <Typography variant="h6" mb={2} sx={{ color: theme.palette.text.primary }}>Individual Agent Reports</Typography>
              {reportData.agentReports?.map((agent, index) => (
                <Box key={agent.agent.id} mb={4}>
                  <Card sx={{ p: 3, borderRadius: 3, boxShadow: 3, bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.6)' : 'rgba(255, 255, 255, 0.95)' }}>
                    {/* Agent Header */}
                    <Box display="flex" alignItems="center" gap={2} mb={3}>
                      <Avatar sx={{ bgcolor: 'primary.main', width: 50, height: 50 }}>
                        {agent.agent.name.charAt(0).toUpperCase()}
                      </Avatar>
                      <Box flex={1}>
                        <Typography variant="h5" sx={{ fontWeight: 600, color: theme.palette.text.primary }}>
                          {agent.agent.name}
                        </Typography>
                        <Typography variant="body2" sx={{ color: theme.palette.text.secondary }}>
                          {agent.agent.email} • {agent.date}
                        </Typography>
                      </Box>
                      <Chip 
                        label={`${agent.officeHours.overallCompliance}% Compliant`}
                        color={agent.officeHours.overallCompliance >= 90 ? 'success' : agent.officeHours.overallCompliance >= 70 ? 'warning' : 'error'}
                        sx={{ fontWeight: 600 }}
                      />
                    </Box>
                    
                    {/* Key Metrics Grid */}
                    <Grid container spacing={2} mb={3}>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} bgcolor="primary.light" borderRadius={2} color="white">
                          <Typography variant="h4">{agent.workMetrics.totalWorkHours}h</Typography>
                          <Typography variant="body2">Work Hours</Typography>
                          <Typography variant="caption">Target: 8h</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} bgcolor="success.light" borderRadius={2} color="white">
                          <Typography variant="h4">{agent.workMetrics.totalTransactions.toLocaleString()}</Typography>
                          <Typography variant="body2">Transactions</Typography>
                          <Typography variant="caption">{agent.workMetrics.transactionsPerHour.toFixed(1)}/hr</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} bgcolor="warning.light" borderRadius={2} color="white">
                          <Typography variant="h4">{agent.breakMetrics.totalBreakHours.toFixed(1)}h</Typography>
                          <Typography variant="body2">Break Hours</Typography>
                          <Typography variant="caption">Target: 1h</Typography>
                        </Box>
                      </Grid>
                      <Grid item xs={12} sm={6} md={3}>
                        <Box textAlign="center" p={2} bgcolor="info.light" borderRadius={2} color="white">
                          <Typography variant="h4">{agent.breakMetrics.totalBreaks}</Typography>
                          <Typography variant="body2">Total Breaks</Typography>
                          <Typography variant="caption">{agent.breakMetrics.averageBreakDuration}min avg</Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    
                    {/* Office Hours Compliance */}
                    <Box mb={3}>
                      <Typography variant="h6" mb={2} sx={{ color: theme.palette.text.primary }}>Office Hours Compliance (9hr schedule: 8hr work + 1hr break)</Typography>
                      <Grid container spacing={2}>
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Work Hours</Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{agent.workMetrics.workHoursCompliance.toFixed(1)}%</Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(agent.workMetrics.workHoursCompliance, 100)} 
                              sx={{ height: 8, borderRadius: 4 }}
                              color={agent.workMetrics.workHoursCompliance >= 90 ? 'success' : agent.workMetrics.workHoursCompliance >= 70 ? 'warning' : 'error'}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Break Hours</Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{agent.breakMetrics.breakHoursCompliance.toFixed(1)}%</Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(agent.breakMetrics.breakHoursCompliance, 100)} 
                              sx={{ height: 8, borderRadius: 4 }}
                              color={agent.breakMetrics.breakHoursCompliance >= 90 ? 'success' : agent.breakMetrics.breakHoursCompliance >= 70 ? 'warning' : 'error'}
                            />
                          </Box>
                        </Grid>
                        <Grid item xs={12} md={4}>
                          <Box>
                            <Box display="flex" justifyContent="space-between" mb={1}>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>Overall</Typography>
                              <Typography variant="body2" sx={{ color: theme.palette.text.primary }}>{agent.officeHours.overallCompliance.toFixed(1)}%</Typography>
                            </Box>
                            <LinearProgress 
                              variant="determinate" 
                              value={Math.min(agent.officeHours.overallCompliance, 100)} 
                              sx={{ height: 8, borderRadius: 4 }}
                              color={agent.officeHours.overallCompliance >= 90 ? 'success' : agent.officeHours.overallCompliance >= 70 ? 'warning' : 'error'}
                            />
                          </Box>
                        </Grid>
                      </Grid>
                    </Box>
                    
                    {/* Project Breakdown */}
                    {Object.keys(agent.projectBreakdown).length > 0 && (
                      <Box mb={3}>
                        <Typography variant="h6" mb={2} sx={{ color: theme.palette.text.primary }}>Project Breakdown</Typography>
                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{
                            maxHeight: 300,
                            borderRadius: 2.5,
                            boxShadow: 'none',
                            bgcolor: 'background.paper',
                            border: (t) => t.palette.mode === 'dark'
                              ? '1px solid rgba(255,255,255,0.08)'
                              : '1px solid #e5e7eb',
                          }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Project</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Hours</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Transactions</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Tasks</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {Object.entries(agent.projectBreakdown).map(([projectName, projectData]) => (
                                <TableRow key={projectName}>
                                  <TableCell sx={{ fontWeight: 500, color: theme.palette.text.primary }}>{projectName}</TableCell>
                                  <TableCell sx={{ color: theme.palette.text.primary }}>{projectData.hours.toFixed(2)}</TableCell>
                                  <TableCell sx={{ color: theme.palette.text.primary }}>{projectData.transactions}</TableCell>
                                  <TableCell>
                                    <Box display="flex" flexWrap="wrap" gap={0.5}>
                                      {Object.entries(projectData.tasks).map(([taskName, taskData]) => (
                                        <Chip 
                                          key={taskName} 
                                          label={`${taskName} (${taskData.hours.toFixed(1)}h, ${taskData.transactions}t)`} 
                                          size="small" 
                                          variant="outlined"
                                        />
                                      ))}
                                    </Box>
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                    
                    {/* Break Details */}
                    {agent.detailedBreaks.length > 0 && (
                      <Box>
                        <Typography variant="h6" mb={2} sx={{ color: theme.palette.text.primary }}>Break Details</Typography>
                        <TableContainer
                          component={Paper}
                          elevation={0}
                          sx={{
                            maxHeight: 300,
                            borderRadius: 2.5,
                            boxShadow: 'none',
                            bgcolor: 'background.paper',
                            border: (t) => t.palette.mode === 'dark'
                              ? '1px solid rgba(255,255,255,0.08)'
                              : '1px solid #e5e7eb',
                          }}>
                          <Table size="small">
                            <TableHead>
                              <TableRow>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Type</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Start Time</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>End Time</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Duration</TableCell>
                                <TableCell sx={{ 
                                  fontWeight: 'bold', 
                                  color: theme.palette.text.primary,
                                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(44, 48, 52, 0.8)' : 'rgba(0, 0, 0, 0.05)'
                                }}>Status</TableCell>
                              </TableRow>
                            </TableHead>
                            <TableBody>
                              {agent.detailedBreaks.map((breakItem) => (
                                <TableRow key={breakItem.id}>
                                  <TableCell>
                                    <Chip 
                                      label={breakItem.type.charAt(0).toUpperCase() + breakItem.type.slice(1)} 
                                      size="small" 
                                      color="primary" 
                                      variant="outlined"
                                    />
                                  </TableCell>
                                  <TableCell sx={{ color: theme.palette.text.primary }}>{new Date(breakItem.startTime).toLocaleTimeString()}</TableCell>
                                  <TableCell sx={{ color: theme.palette.text.primary }}>{breakItem.endTime ? new Date(breakItem.endTime).toLocaleTimeString() : 'Active'}</TableCell>
                                  <TableCell sx={{ color: theme.palette.text.primary }}>{breakItem.duration ? `${breakItem.duration}min` : '-'}</TableCell>
                                  <TableCell>
                                    <Chip 
                                      label={breakItem.isActive ? 'Active' : 'Completed'} 
                                      size="small" 
                                      color={breakItem.isActive ? 'warning' : 'success'}
                                    />
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </TableContainer>
                      </Box>
                    )}
                  </Card>
                </Box>
              ))}
            </>
          ) : selectedReport === 'breaksLeisure' ? (
            <Box>
              {/* Summary Statistics */}
              {reportData.summary && (
                <Box
                  sx={{
                    display: 'grid',
                    gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', md: 'repeat(4, 1fr)' },
                    gap: 2.5,
                    mb: 3,
                  }}
                >
                  <StatCard label="Total Breaks" value={reportData.summary.totalBreaks} icon={<CoffeeIcon />} accent="#3b82f6" />
                  <StatCard label="Total Hours" value={reportData.summary.totalHours} icon={<TimeIcon />} accent="#10b981" />
                  <StatCard label="Avg Hours/Break" value={reportData.summary.averageHours} icon={<TimeIcon />} accent="#8b5cf6" />
                  <StatCard label="Avg Minutes/Break" value={reportData.summary.averageDuration} icon={<TimeIcon />} accent="#f59e0b" />
                </Box>
              )}

              {/* Breaks by Type Chart */}
              {reportData.breaksByType && (
                <Box mb={4}>
                  <Typography variant="h6" mb={2}>Breaks by Type</Typography>
                  <Box width="100%" height={300}>
                    <ResponsiveContainer>
                      <PieChart animationDuration={500}>
                        <Pie
                          data={Object.entries(reportData.breaksByType).map(([type, data]) => ({
                            name: type.charAt(0).toUpperCase() + type.slice(1),
                            value: data.count,
                            duration: data.duration
                          }))}
                          dataKey="value"
                          nameKey="name"
                          label={({ name, value, percent }) => `${name}: ${value} (${(percent * 100).toFixed(0)}%)`}
                          labelLine={false}
                        >
                          {Object.entries(reportData.breaksByType).map((entry, idx) => (
                            <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                          ))}
                        </Pie>
                        <Legend verticalAlign="bottom" height={36} />
                        <RechartTooltip />
                      </PieChart>
                    </ResponsiveContainer>
                  </Box>
                </Box>
              )}

              {/* Detailed Breaks Table */}
              {reportData.detailedBreaks && (
                <Box>
                  <Typography variant="h6" mb={2}>Detailed Breaks</Typography>
                  <TableContainer
              component={Paper}
              elevation={0}
              sx={{
                maxHeight: 440,
                borderRadius: 2.5,
                boxShadow: 'none',
                bgcolor: 'background.paper',
                border: (t) => t.palette.mode === 'dark'
                  ? '1px solid rgba(255,255,255,0.08)'
                  : '1px solid #e5e7eb',
              }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          {['Employee','Type','Description','Start Time','End Time','Duration (Hours)','Date','Status'].map((header) => (
                            <TableCell
                              key={header}
                              sx={{
                                backgroundColor: theme.palette.mode === 'dark' 
                              ? 'rgba(44, 48, 52, 0.8)' 
                              : 'rgba(0, 0, 0, 0.05)',
                            color: theme.palette.mode === 'dark' ? 'white' : '#2C3034',
                                fontWeight: 'bold',
                                whiteSpace: 'nowrap',
                                position: 'sticky',
                                top: 0,
                              }}
                            >
                              {header}
                            </TableCell>
                          ))}
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {reportData.detailedBreaks.map((row) => (
                          <TableRow key={row.id} hover>
                            <TableCell sx={{ fontWeight: 500 }}>{row.employee}</TableCell>
                            <TableCell>
                              <Chip 
                                label={row.type.charAt(0).toUpperCase() + row.type.slice(1)} 
                                size="small" 
                                color="primary" 
                                variant="outlined"
                              />
                            </TableCell>
                            <TableCell>{row.description || '-'}</TableCell>
                            <TableCell>{new Date(row.startTime).toLocaleString()}</TableCell>
                            <TableCell>{row.endTime ? new Date(row.endTime).toLocaleString() : 'Active'}</TableCell>
                            <TableCell>{row.durationHours}</TableCell>
                            <TableCell>{row.date}</TableCell>
                            <TableCell>
                              <Chip 
                                label={row.isActive ? 'Active' : 'Completed'} 
                                size="small" 
                                color={row.isActive ? 'warning' : 'success'}
                              />
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}
            </Box>
          ) : selectedReport === 'consolidatedAgentDaily' ? (
            <>
              <Box
                sx={{
                  mb: 2,
                  display: 'flex',
                  gap: 1.5,
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  px: 2,
                  py: 1.5,
                  bgcolor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : '#fafbfc',
                  border: theme.palette.mode === 'dark' ? '1px solid rgba(255,255,255,0.08)' : '1px solid #e5e7eb',
                  borderRadius: 2,
                }}
              >
                <TextField
                  type="date"
                  label="Select Date"
                  size="small"
                  value={dailyDate}
                  onChange={(e) => setDailyDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'en-GB' }}
                />
                <Button
                  variant="contained"
                  onClick={() => fetchReportData('consolidatedAgentDaily', { date: dailyDate })}
                  disabled={!dailyDate}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Apply Filter
                </Button>
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportReportCsv}
                  disabled={!reportData?.rows || reportData.rows.length === 0}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Download Report
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  maxHeight: 440,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  bgcolor: 'background.paper',
                  border: (t) => t.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid #e5e7eb',
                }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {['Project','Potential ID','Task','Employee','Task Status','Total Hours','Worked Hours','Transactions','Break Hours','Other Hours'].map(h => (
                        <TableCell key={h} sx={{fontWeight:'bold', position:'sticky', top:0, backgroundColor: theme.palette.grey[200]}}>{h}</TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.rows?.map((row,idx)=>(
                      <TableRow key={idx} hover>
                        <TableCell>{row.projectName}</TableCell>
                        <TableCell>{row.potentialId}</TableCell>
                        <TableCell>{row.taskName}</TableCell>
                        <TableCell>{row.teamMember}</TableCell>
                        <TableCell>{row.taskStatus}</TableCell>
                        <TableCell>{row.totalHours}</TableCell>
                        <TableCell>{row.workedHours}</TableCell>
                        <TableCell>{row.transactions}</TableCell>
                        <TableCell>{row.breakHours}</TableCell>
                        <TableCell>{row.otherHours}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : selectedReport === 'assigneeTasksReport' ? (
            <>
              <Box mb={2} display="flex" gap={2} flexWrap="wrap" alignItems="center">
                <TextField
                  type="date"
                  label="Start Date"
                  size="small"
                  value={assigneeStartDate}
                  onChange={(e) => setAssigneeStartDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'en-GB' }}
                />
                <TextField
                  type="date"
                  label="End Date"
                  size="small"
                  value={assigneeEndDate}
                  onChange={(e) => setAssigneeEndDate(e.target.value)}
                  InputLabelProps={{ shrink: true }}
                  inputProps={{ lang: 'en-GB' }}
                />
                <Button
                  variant="contained"
                  onClick={() => {
                    const params = {};
                    if (assigneeStartDate) params.startDate = assigneeStartDate;
                    if (assigneeEndDate) params.endDate = assigneeEndDate;
                    fetchReportData('assigneeTasksReport', params);
                  }}
                  size="small"
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Apply Filter
                </Button>
                {(assigneeStartDate || assigneeEndDate) && (
                  <IconButton
                    size="small"
                    onClick={() => {
                      setAssigneeStartDate('');
                      setAssigneeEndDate('');
                      fetchReportData('assigneeTasksReport');
                    }}
                    sx={{
                      color: 'error.main',
                      '&:hover': { bgcolor: 'error.light', color: 'white' },
                    }}
                    title="Clear date filter"
                  >
                    <ClearIcon />
                  </IconButton>
                )}
                <Box sx={{ flexGrow: 1 }} />
                <Button
                  variant="outlined"
                  size="small"
                  startIcon={<DownloadIcon />}
                  onClick={exportReportCsv}
                  disabled={!reportData || reportData.length === 0}
                  sx={{ textTransform: 'none', fontSize: '0.8rem' }}
                >
                  Download Report
                </Button>
              </Box>
              <TableContainer
                component={Paper}
                elevation={0}
                sx={{
                  maxHeight: 500,
                  borderRadius: 2.5,
                  boxShadow: 'none',
                  bgcolor: 'background.paper',
                  border: (t) => t.palette.mode === 'dark'
                    ? '1px solid rgba(255,255,255,0.08)'
                    : '1px solid #e5e7eb',
                }}
              >
                <Table size="small" stickyHeader sx={{ minWidth: 1000 }}>
                  <TableHead>
                    <TableRow>
                      {[
                        { label: 'Project', minWidth: 200 },
                        { label: 'Task', minWidth: 240 },
                        { label: 'Timeline', minWidth: 160 },
                        { label: 'Requester', minWidth: 140 },
                        { label: 'Status', minWidth: 120 },
                        { label: 'Project Manager', minWidth: 160 },
                        { label: 'Assignees', minWidth: 180 },
                        { label: 'Iter.', minWidth: 60, align: 'center' },
                      ].map((col) => (
                        <TableCell
                          key={col.label}
                          align={col.align || 'left'}
                          sx={{ whiteSpace: 'nowrap', minWidth: col.minWidth }}
                        >
                          {col.label}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {reportData.map((row, idx) => {
                      const startDate = row.startDate ? new Date(row.startDate).toLocaleDateString('en-GB') : null;
                      const endDate = row.endDate ? new Date(row.endDate).toLocaleDateString('en-GB') : null;
                      const timeline = startDate || endDate
                        ? `${startDate || '—'} → ${endDate || '—'}`
                        : '—';
                      const assignees = row.taskAssigned ? row.taskAssigned.split(',').map(a => a.trim()).filter(Boolean) : [];
                      const assigneesLabel = assignees.length === 0
                        ? '—'
                        : assignees.length === 1
                          ? assignees[0]
                          : `${assignees[0]} +${assignees.length - 1}`;
                      return (
                        <TableRow key={idx} hover>
                          <TableCell sx={{ fontWeight: 500, color: theme.palette.text.primary, maxWidth: 280 }}>
                            <Tooltip title={row.project || ''} placement="top-start">
                              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row.project || '—'}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, maxWidth: 320 }}>
                            <Tooltip title={row.task || ''} placement="top-start">
                              <Box sx={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                                {row.task || '—'}
                              </Box>
                            </Tooltip>
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, whiteSpace: 'nowrap' }}>
                            {timeline}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, whiteSpace: 'nowrap' }}>
                            {row.requester || '—'}
                          </TableCell>
                          <TableCell>
                            <Chip
                              label={(row.taskStatus || 'N/A').replace(/_/g, ' ')}
                              size="small"
                              color={row.taskStatus === 'completed' ? 'success' : row.taskStatus === 'in_progress' ? 'primary' : 'default'}
                              variant="outlined"
                              sx={{ textTransform: 'capitalize', fontWeight: 500 }}
                            />
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, whiteSpace: 'nowrap' }}>
                            {row.projectManager || '—'}
                          </TableCell>
                          <TableCell sx={{ color: theme.palette.text.primary, whiteSpace: 'nowrap' }}>
                            {assignees.length > 1 ? (
                              <Tooltip title={assignees.join(', ')} placement="top">
                                <Box component="span" sx={{ cursor: 'help', textDecoration: 'underline', textDecorationStyle: 'dotted' }}>
                                  {assigneesLabel}
                                </Box>
                              </Tooltip>
                            ) : (
                              <Box component="span">{assigneesLabel}</Box>
                            )}
                          </TableCell>
                          <TableCell align="center" sx={{ color: theme.palette.text.primary }}>{row.iterations || 1}</TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </TableContainer>
            </>
          ) : (
            <Box width="100%" height={300}>
              <ResponsiveContainer>
                {['tasksByStatus','projectsByStatus'].includes(selectedReport) ? (
                  <PieChart animationDuration={500}>
                    <Pie
                      data={reportData}
                      dataKey={reportConfig[selectedReport].valueKey}
                      nameKey={reportConfig[selectedReport].labelKey}
                      label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                      labelLine={false}
                    >
                      {reportData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Pie>
                    <Legend verticalAlign="bottom" height={36} />
                    <RechartTooltip />
                  </PieChart>
                ) : (
                  <BarChart
                    data={reportData}
                    margin={{ top: 20, right: 30, left: 20, bottom: 5 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis dataKey={reportConfig[selectedReport].labelKey} />
                    <YAxis />
                    <RechartTooltip />
                    <Legend verticalAlign="bottom" height={36} />
                    <Bar dataKey={reportConfig[selectedReport].valueKey}>
                      {reportData.map((entry, idx) => (
                        <Cell key={`cell-${idx}`} fill={COLORS[idx % COLORS.length]} />
                      ))}
                    </Bar>
                  </BarChart>
                )}
              </ResponsiveContainer>
            </Box>
          )}
        </Box>
      </Fade>
      )}
      </Box>
    </Box>
  );
};

export default Dashboard; 